import * as React from 'react';
import styled, { keyframes, ThemeProvider } from 'styled-components';
import { rem } from 'polished';
import {
  FlexRow,
  FlexRowCenterVertical,
  FlexColCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { NoItemsWrapper } from '../General/NoItemsWrapper';
import { ZemplinLoaderWrapper } from '../../..';
import { ArticleCard } from './ArticleCard';
import { NotFound } from '../General/NotFound';
import { getArticleDetailInfo } from '../../../utilities/selectors';
import { TextP } from '../../Defaults/Text';
import { DotDelimeter } from '../General/DotDelimeter';
import { Modal } from '../../Defaults/Modal/Modal';
import { ModalMobile } from '../../Defaults/Modal/ModalMobile';
import { ImageGallery } from '../Product/ImageGallery';
import { getImagePath } from '../../../utilities';

interface Props {
  article: any;
  photogallery?: any;
  apiUrl: string;
}

function Article({ article, photogallery, apiUrl }: Props) {
  if (!article) {
    return <NotFound text={__('Tento článok nebolo možné nájsť')} />;
  }
  const { title, date, category, content, downloads } = getArticleDetailInfo(
    article,
  );

  const [imageModalVisible, setImageModalVisible] = React.useState(false);
  const [modalCurrentImage, setModalCurrentImage] = React.useState(0);

  const fullscreenImages =
    photogallery &&
    photogallery.map(doc => {
      const img = getImagePath(apiUrl, doc.document_path, {
        width: 800,
        height: 600,
      });
      const thumbImg = getImagePath(apiUrl, doc.document_path, {
        width: 80,
        height: 80,
      });
      return {
        original: img,
        thumbnail: thumbImg,
        name: '',
      };
    });

  const closeImageModal = () => {
    setImageModalVisible(false);
  };

  const setCurrentImage = c => {
    setModalCurrentImage(c);
  };

  const openModal = (openOnIndex: number = 0) => {
    setImageModalVisible(true);
    setModalCurrentImage(openOnIndex);
  };

  return (
    <MainContent>
      <CmsTitle>{title}</CmsTitle>
      <DateCategoryWrapper>
        <Date>{date}</Date>
        {false && (
          <>
            <DotDelimeter />
            <CategoryName>{'Spojovací materiál'}</CategoryName>
          </>
        )}
      </DateCategoryWrapper>
      <Body
        dangerouslySetInnerHTML={{
          __html: typeof content === 'object' ? '' : content,
        }}
      />
      <GalleryWrapper>
        {fullscreenImages &&
          fullscreenImages.map((image, i) => {
            if (i < 6) {
              return (
                <Thumb
                  src={image.thumbnail}
                  alt={`Obrázok`}
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => openModal(i)}
                />
              );
            }
            if (i === 6) {
              return (
                <MoreThumb
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={() => openModal(i)}
                >
                  ...
                </MoreThumb>
              );
            }
            return null;
          })}
      </GalleryWrapper>
      {downloads && downloads.length > 0 ? <h5>{__('Na stiahnutie:')}</h5> : ''}
      {downloads &&
        downloads.map(doc => {
          return (
            <>
              <a
                href={`https://www.zemplin.sk/api/v1/file?query=${doc.document_path}`}
                target="_blank"
              >
                {doc.document_description} ({doc.document_name})
              </a>
              <br />
            </>
          );
        })}
      {imageModalVisible && (
        <Modal size="big" showX={false} closeModal={closeImageModal}>
          <ModalDesktopWrapper>
            <ImageGallery
              closeModal={closeImageModal}
              setCurrentImage={setCurrentImage}
              items={fullscreenImages}
              currentIndex={modalCurrentImage}
              isVertical={false}
            />
          </ModalDesktopWrapper>

          <ModalMobileWrapper>
            <ModalMobile
              src={fullscreenImages}
              currentIndex={modalCurrentImage}
              closeModal={closeImageModal}
              setCurrentImage={setCurrentImage}
            />
          </ModalMobileWrapper>
        </Modal>
      )}
    </MainContent>
  );
}

const ModalDesktopWrapper = styled.div`
  display: flex;
  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const ModalMobileWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mediab.l925`
    display: flex;
  `}
`;

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const MainContent = styled.div`
  animation: ${animateIn} 0.2s ease;
  max-width: ${rem(800)};
  max-height: 100%;
  width: 100%;
  margin: 0 auto;
  padding: ${rem(56)} 0 ${rem(56)};

  ${({ theme }) => theme.mediab.m580`
     padding: 0 ;
     margin: ${rem(32)} auto ${rem(56)};
  `}
`;

const CmsTitle = styled.h1`
  margin: 0;
  margin-bottom: ${rem(32)};
  font-weight: 400;
  font-size: ${rem(36)};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  line-height: 44px;

  ${({ theme }) => theme.mediab.m580`
    font-size: ${rem(24)};
    line-height: 32px;
  `}
`;

const DateCategoryWrapper = styled(FlexRowCenterVertical)`
  border-top: ${({ theme }) => theme.borders.primary};
  border-bottom: ${({ theme }) => theme.borders.primary};
  padding: ${rem(24)} 0;
  margin-bottom: ${rem(56)};

  ${({ theme }) => theme.mediab.m580`
    margin-bottom: ${rem(32)};
    margin-left: -${rem(16)};
    margin-right: -${rem(16)};
    padding: ${rem(24)} ${rem(16)};
  `}
`;

const Date = styled(TextP)`
  font-size: ${rem(12)};
  font-family: ${({ theme }) => theme.font.tertiary};
  color: ${({ theme }) => theme.colors.placeholder};
  line-height: ${rem(20)};
`;

const CategoryName = styled(Date)``;

const Body = styled.div`
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 400;
  line-height: ${rem(26)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const GalleryWrapper = styled(FlexRowCenterVertical)`
  margin: ${rem(16)} 0 ${rem(48)};
  flex-flow: row wrap;
`;

const Thumb = styled.img`
  width: ${rem(80)};
  height: ${rem(80)};
  margin-right: ${rem(16)};
  margin-bottom: ${rem(16)};
  cursor: pointer;
  border-radius: ${rem(4)};
`;

const MoreThumb = styled(FlexColCenter)`
  width: ${rem(80)};
  height: ${rem(80)};
  margin-right: ${rem(16)};
  margin-bottom: ${rem(16)};
  border: ${rem(1)} solid #ccc;
  cursor: pointer;
  border-radius: ${rem(4)};
  font-size: ${rem(32)};
  color: #ccc;
`;

export { Article };
